import React, { useState } from 'react';

import { useNavigate } from '../i18n/router';
import { useTranslation } from 'react-i18next';
import { signUp, checkSociete } from '../Services';
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from './SnackbarProvider';

type Groupe = {
  id: number;
  id_societe: number;
  nom: string;
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    backgroundColor: '#f8f9fa',
    '&:hover': { backgroundColor: '#f0f1f2' },
    '&.Mui-focused': { backgroundColor: 'white' },
  },
};

const SignUpScreen = () => {
  const { t } = useTranslation('auth');
  const [nom, setNom] = useState('');
  const [isNomDisabled, setIsNomDisabled] = useState(false);
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordStrengthColor, setPasswordStrengthColor] = useState('#e53935');
  const [passwordHelperText, setPasswordHelperText] = useState<string>(
    t('signup.password.default')
  );
  const [groupes, setGroupes] = useState<Groupe[]>([]);
  const [selectedGroupe, setSelectedGroupe] = useState<number | string>('');
  const [idSociete, setIdSociete] = useState<number | null>(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { showSnackbar } = useSnackbar();

  const isPasswordSecure = (password: string) => password.length >= 8;
  const isEmailValid = (emailVal: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal);
  const isPhoneValid = (phone: string) => /^[0-9]+$/.test(phone);

  const getPasswordStrength = (password: string) => {
    const length = password.length;
    if (length >= 8) {
      setPasswordHelperText(t('signup.password.strong'));
      return '#2e7d32';
    }
    if (length >= 4) {
      setPasswordHelperText(t('signup.password.medium'));
      return '#ff9800';
    }
    setPasswordHelperText(t('signup.password.weak'));
    return '#e53935';
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setMotDePasse(password);
    setPasswordStrengthColor(getPasswordStrength(password));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (isNomDisabled) {
      setNom('');
      setIsNomDisabled(false);
      setGroupes([]);
      setIdSociete(null);
      setSelectedGroupe('');
    }
  };

  const handleCheckSociete = async () => {
    if (email && email.includes('@')) {
      const domain = email.split('@')[1];
      try {
        const response = await checkSociete(domain);
        if (response.societe) {
          setNom(response.societe.nom);
          setIsNomDisabled(true);
          setIdSociete(response.societe.id);
        }
        if (response.groupes && response.groupes.length > 0) {
          setGroupes(response.groupes);
          if (response.groupes.length === 1) {
            setSelectedGroupe(response.groupes[0].id);
          }
        }
      } catch (error) {
        console.error('Erreur lors du check de société', error);
      }
    }
  };

  const handleSignUp = async () => {
    if (!nom.trim() || !email.trim() || !motDePasse.trim() || !telephone.trim()) {
      showSnackbar(t('signup.errors.emptyFields'), 'error');
      return;
    }
    if (!isEmailValid(email)) {
      showSnackbar(t('signup.errors.invalidEmail'), 'error');
      return;
    }
    if (email !== confirmEmail) {
      showSnackbar(t('signup.errors.emailMismatch'), 'error');
      return;
    }
    if (!isPhoneValid(telephone)) {
      showSnackbar(t('signup.errors.invalidPhone'), 'error');
      return;
    }
    if (!isPasswordSecure(motDePasse)) {
      showSnackbar(t('signup.errors.weakPassword'), 'error');
      return;
    }
    if (idSociete && groupes.length > 0 && !selectedGroupe) {
      showSnackbar(t('signup.errors.missingGroup'), 'error');
      return;
    }
    try {
      await signUp({
        nom,
        email,
        motDePasse,
        telephone,
        idSociete: idSociete ? idSociete : undefined,
        idGroupe: selectedGroupe ? selectedGroupe : undefined,
      });
      showSnackbar(t('signup.success'), 'success');
      navigate(-1);
    } catch (error) {
      showSnackbar(t('signup.errors.alreadyExists'), 'error');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'linear-gradient(180deg, #FFF8EC 0%, #FAF3E2 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          maxWidth: isDesktop ? 900 : 420,
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          backgroundColor: 'white',
        }}
      >
        {/* Left panel - branding (desktop only) */}
        {isDesktop && (
          <Box
            sx={{
              flex: 1,
              background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 5,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.06)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -40,
                left: -40,
                width: 160,
                height: 160,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.04)',
              }}
            />

            <Box
              component="img"
              src="/logo-betraveling.svg"
              alt="BeTravel"
              sx={{
                width: 120,
                height: 120,
                mb: 3,
                objectFit: 'contain',
                borderRadius: 2,
                boxShadow: '0 12px 32px rgba(216, 157, 47, 0.28)',
              }}
            />
            <Typography
              variant="h5"
              sx={{ color: 'white', fontWeight: 700, textAlign: 'center', mb: 1.5, lineHeight: 1.3, fontFamily: "'Fraunces', serif" }}
            >
              {t('signup.brandTagline')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.75)', textAlign: 'center', maxWidth: 280, lineHeight: 1.6 }}
            >
              {t('signup.brandSubtitle')}
            </Typography>
          </Box>
        )}

        {/* Right panel - form */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflowY: 'auto',
            maxHeight: '95vh',
          }}
        >
          {/* Back button + Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                backgroundColor: '#f5f5f5',
                '&:hover': { backgroundColor: '#eee' },
                width: 40,
                height: 40,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 20, color: '#555' }} />
            </IconButton>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
                {t('signup.title')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', fontSize: '0.8rem' }}>
                {t('signup.subtitle')}
              </Typography>
            </Box>
          </Box>

          {/* Mobile logo */}
          {!isDesktop && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box
                component="img"
                src="/logo-betraveling.svg"
                alt="BeTravel"
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  objectFit: 'contain',
                  borderRadius: 1.5,
                  boxShadow: '0 4px 12px rgba(216, 157, 47, 0.30)',
                }}
              />
            </Box>
          )}

          <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Nom */}
            <TextField
              fullWidth
              label={t('signup.fields.name')}
              variant="outlined"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              disabled={isNomDisabled}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ color: '#999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />

            {/* Email */}
            <TextField
              fullWidth
              label={t('signup.fields.email')}
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleCheckSociete}
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />

            {/* Confirm email */}
            {email && (
              <TextField
                fullWidth
                label={t('signup.fields.confirmEmail')}
                variant="outlined"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />
            )}

            {/* Groupe select */}
            {groupes.length > 0 && (
              <FormControl fullWidth sx={inputSx}>
                <InputLabel id="groupe-select-label">{t('signup.fields.group')}</InputLabel>
                <Select
                  labelId="groupe-select-label"
                  value={selectedGroupe}
                  label={t('signup.fields.group')}
                  onChange={(e) => setSelectedGroupe(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <GroupsOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  }
                  sx={{ borderRadius: 3 }}
                >
                  {groupes.map((groupe) => (
                    <MenuItem key={groupe.id} value={groupe.id}>
                      {groupe.nom}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Telephone */}
            <TextField
              fullWidth
              label={t('signup.fields.phone')}
              variant="outlined"
              value={telephone}
              onChange={(e) => setTelephone(e.target.value)}
              type="tel"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />

            {/* Password */}
            <TextField
              fullWidth
              label={t('signup.fields.password')}
              variant="outlined"
              value={motDePasse}
              onChange={handlePasswordChange}
              type={passwordVisible ? 'text' : 'password'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setPasswordVisible(!passwordVisible)} edge="end" size="small">
                      {passwordVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />

            {/* Password strength */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#eee',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: motDePasse.length >= 8 ? '100%' : motDePasse.length >= 4 ? '60%' : motDePasse.length > 0 ? '25%' : '0%',
                    height: '100%',
                    borderRadius: 3,
                    backgroundColor: passwordStrengthColor,
                    transition: 'all 0.3s ease',
                  }}
                />
              </Box>
            </Box>
            <Typography variant="caption" sx={{ color: '#888', mt: -0.5 }}>
              {passwordHelperText}
            </Typography>

            {/* Submit */}
            <Button
              variant="contained"
              fullWidth
              onClick={handleSignUp}
              sx={{
                mt: 1,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
                boxShadow: '0 4px 14px rgba(46,125,50,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #B5811F 0%, #8C6418 100%)',
                  boxShadow: '0 6px 20px rgba(46,125,50,0.4)',
                },
              }}
            >
              {t('signup.submit')}
            </Button>

            {/* Back link */}
            <Button
              variant="text"
              fullWidth
              onClick={() => navigate(-1)}
              sx={{
                textTransform: 'none',
                color: '#888',
                fontWeight: 500,
                '&:hover': { color: '#555', backgroundColor: 'transparent' },
              }}
            >
              {t('backToLogin')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SignUpScreen;
